import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, AuthContextType, AppRole } from '@/types/auth';
import { toast } from 'sonner';


const SESSION_KEY = 'lovable_session_id';
const SESSION_CHECK_INTERVAL = 30000; // 30 seconds

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionCheckRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const isValidatingRef = useRef(false);

  // Generate or retrieve session ID
  const getOrCreateSessionId = useCallback(() => {
    let sessionId = localStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  }, []);

  // Clear session ID from localStorage
  const clearSessionId = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
  }, []);

  // Register session in database
  const registerSession = useCallback(async (userId: string) => {
    const sessionId = getOrCreateSessionId();
    try {
      await supabase.rpc('set_user_session', {
        p_user_id: userId,
        p_session_id: sessionId,
      });
    } catch (error) {
      console.warn('Failed to register session:', error);
    }
  }, [getOrCreateSessionId]);

  // Validate current session is still active
  const validateSession = useCallback(async (userId: string): Promise<boolean> => {
    const sessionId = localStorage.getItem(SESSION_KEY);
    if (!sessionId) return false;

    try {
      const { data, error } = await supabase.rpc('validate_user_session', {
        p_user_id: userId,
        p_session_id: sessionId,
      });
      
      if (error) {
        console.warn('Session validation error:', error);
        return true; // Assume valid on error to avoid unnecessary logouts
      }
      
      return data === true;
    } catch (error) {
      console.warn('Session validation failed:', error);
      return true; // Assume valid on error
    }
  }, []);

  // Force logout due to session invalidation
  const forceLogout = useCallback(async () => {
    // Clean up realtime subscription
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
      sessionCheckRef.current = null;
    }
    clearSessionId();
    await supabase.auth.signOut();
    setUser(null);
    toast.error('Session terminée', {
      description: 'Vous avez été déconnecté car une connexion a été établie depuis un autre appareil.',
      duration: 6000,
    });
  }, [clearSessionId]);

  // Start periodic session validation
  const startSessionCheck = useCallback((userId: string) => {
    // Clear any existing interval
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
    }

    sessionCheckRef.current = setInterval(async () => {
      if (isValidatingRef.current) return;
      isValidatingRef.current = true;

      try {
        const isValid = await validateSession(userId);
        if (!isValid) {
          await forceLogout();
        }
      } finally {
        isValidatingRef.current = false;
      }
    }, SESSION_CHECK_INTERVAL);
  }, [validateSession, forceLogout]);

  // Stop session check and realtime subscription
  const stopSessionCheck = useCallback(() => {
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
      sessionCheckRef.current = null;
    }
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
  }, []);

  // Subscribe to realtime changes on user's session
  const subscribeToSessionChanges = useCallback((userId: string) => {
    // Clean up any existing subscription
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    const localSessionId = localStorage.getItem(SESSION_KEY);

    realtimeChannelRef.current = supabase
      .channel(`user-session-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const newSessionId = payload.new?.current_session_id;
          // If session changed and it's not our session, force logout
          if (newSessionId && newSessionId !== localSessionId) {
            void forceLogout();
          }
        }
      )
      .subscribe();
  }, [forceLogout]);

  const fetchUserProfile = useCallback(async (authId: string): Promise<UserProfile | null> => {
    // Use view that fetches role from user_roles table (RBAC)
    const { data, error } = await supabase
      .from('users_with_roles')
      .select('*')
      .eq('auth_id', authId)
      .single();

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    return {
      id: data.id,
      auth_id: data.auth_id,
      role: data.role as AppRole,
      manager_id: data.manager_id,
      name: data.name,
      email: data.email,
      phone: data.phone,
      active: data.active,
      created_at: data.created_at,
      updated_at: data.updated_at,
    } as UserProfile;
  }, []);

  const handleUserSession = useCallback(async (authUserId: string | null) => {
    if (!authUserId) {
      setUser(null);
      setLoading(false);
      return;
    }

    const profile = await fetchUserProfile(authUserId);

    if (profile && !profile.active) {
      stopSessionCheck();
      clearSessionId();
      await supabase.auth.signOut();
      setUser(null);
      setLoading(false);
      toast.error('Compte désactivé', {
        description: 'Votre compte a été désactivé. Veuillez contacter un administrateur.',
      });
      return;
    }

    if (profile) {
      // Register this session and start monitoring
      await registerSession(profile.id);
      subscribeToSessionChanges(profile.id); // Realtime for instant detection
      startSessionCheck(profile.id); // Fallback periodic check
    }

    setUser(profile);
    setLoading(false);
  }, [fetchUserProfile, registerSession, startSessionCheck, subscribeToSessionChanges, stopSessionCheck, clearSessionId]);

  useEffect(() => {
    let isMounted = true;
    let initialCheckDone = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      // Skip if this is the initial SIGNED_IN event triggered by getSession
      if (!initialCheckDone && event === 'INITIAL_SESSION') {
        return;
      }

      // Use setTimeout to prevent Supabase deadlock
      setTimeout(() => {
        if (!isMounted) return;
        void handleUserSession(session?.user?.id ?? null);
      }, 0);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      initialCheckDone = true;
      void handleUserSession(session?.user?.id ?? null);
    }).catch(() => {
      // Ensure loading is set to false even on error
      if (isMounted) {
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      stopSessionCheck();
    };
  }, [handleUserSession, stopSessionCheck]);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    
    // Generate new session ID for this login
    clearSessionId();
    const newSessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, newSessionId);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setLoading(false);
      clearSessionId();
      return { error };
    }

    // Fetch profile immediately after successful sign in
    if (data.user) {
      await handleUserSession(data.user.id);
    }

    return { error: null };
  };

  const signOut = async () => {
    stopSessionCheck();
    
    // Clear session in database
    if (user?.id) {
      try {
        await supabase.rpc('clear_user_session', {
          p_user_id: user.id,
        });
      } catch (error) {
        console.warn('Failed to clear session:', error);
      }
    }
    
    clearSessionId();
    await supabase.auth.signOut();
    setUser(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    return { error };
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
